<template>
	<div class="social-login">
		<div class="divider">
			<span>其他登录方式</span>
		</div>

		<button class="btn btn-wechat" @click="handleWechatLogin" :disabled="loading">
			<i v-if="loading" class="el-icon-loading"></i>
			<i v-else class="el-icon-chat-dot-round"></i>
			{{ loading ? '微信登录中...' : '微信快捷登录' }}
		</button>
	</div>
</template>

<script>
	export default {
		name: 'WechatLogin',
		data() {
			return {
				loading: false,
				wxLoginProcessing: false
			};
		},
		methods: {
			// 检查是否在微信浏览器中
			isWeChatBrowser() {
				const ua = navigator.userAgent.toLowerCase();
				return ua.indexOf('micromessenger') > -1;
			},

			// 清理URL中的code参数
			cleanUrlCode() {
				try {
					const url = new URL(window.location.href);
					const searchParams = new URLSearchParams(url.search);

					if (searchParams.has('code') || searchParams.has('state')) {
						searchParams.delete('code');
						searchParams.delete('state');

						const newSearch = searchParams.toString();
						const newUrl = url.origin + url.pathname + (newSearch ? '?' + newSearch : '') + url.hash;

						window.history.replaceState({}, document.title, newUrl);
					}
				} catch (e) {
					this.$warning('清理URL失败');
				}
			},

			// 处理微信登录
			async handleWechatLogin() {
				if (this.loading) return;

				// 检查是否在微信浏览器中
				if (!this.isWeChatBrowser()) {
					this.$warning('请在微信浏览器中打开此页面进行微信登录');
					return;
				}

				this.loading = true;

				try {
					// 检查当前URL是否有微信回调的code参数
					const urlParams = new URLSearchParams(window.location.search);
					const code = urlParams.get('code');
					const state = urlParams.get('state');

					if (code) {
						// 处理微信回调
						await this.handleWechatCallback(code, state);
					} else {
						// 开始微信OAuth授权
						await this.startWechatOAuth();
					}
				} catch (error) {
					this.$warning('微信登录失败' + (error.message || '网络错误'));
				} finally {
					this.loading = false;
				}
			},

			// 启动微信OAuth授权
			async startWechatOAuth() {
				try {
					// 获取当前页面URL作为回调地址
					const currentUrl = window.location.href.split('#')[0];
					const timestamp = Date.now();
					const state = 'wx_pc_' + timestamp;

					// 调用后端获取授权URL
					const res = await this.$post('wechatAuthUrl', {
						redirect_uri: currentUrl,
						scope: 'snsapi_userinfo',
						state: state
					});

					if (res.data && res.data.auth_url) {
						// 跳转到微信授权页面
						window.location.href = res.data.auth_url;
					} else {
						this.$warning('获取微信授权链接失败');
					}
				} catch (error) {
					this.$warning('微信授权失败，请稍后重试');
				}
			},

			// 处理微信回调
			async handleWechatCallback(code, state) {
				if (!code) {
					this.$warning('微信授权失败，未获取到授权码');
					return;
				}

				if (this.wxLoginProcessing) {
					return;
				}

				this.wxLoginProcessing = true;

				try {
					// 调用微信登录接口
					const res = await this.$post('wechatLogin', {
						code: code,
						state: state || ''
					});
					
					// 清理URL
					this.cleanUrlCode();

					if (res.extra && res.extra.UserCheck) {
						// 登录成功
						this.handleLoginSuccess(res);

					} else if (res.extra && res.extra.need_bind_phone) {
						// 需要绑定手机号 - 存储微信信息到缓存
						if (res.data && res.data.wechat_info) {
							this.$vuex('wechat_info', res.data.wechat_info, true);
							// 触发事件通知父组件显示绑定弹窗
							this.$emit('need-bind-phone', res.data.wechat_info);
						}

					} else {
						this.$warning('微信登录失败');
					}
				} catch (error) {
					this.$warning('微信登录失败: ' + (error.message || '网络错误'));
				} finally {
					this.wxLoginProcessing = false;
				}
			},

			// 处理登录成功
			handleLoginSuccess(res) {
				// 保存登录信息
				try {
					if (this.$apimit && res.extra.UserCheck) {
						this.$apimit.set(res.extra.UserCheck);
					}

					if (this.$vuex) {
						if (res.data) {
							this.$vuex('UserInfo', res.data, true);
						}
						if (res.extra.UserCheck) {
							this.$vuex('UserCheck', res.extra.UserCheck, true);
						}
					}

					// 保存到localStorage
					if (res.extra.UserCheck) {
						localStorage.setItem('UserCheck', JSON.stringify(res.extra.UserCheck));
					}
					if (res.data) {
						localStorage.setItem('UserInfo', JSON.stringify(res.data));
					}
				} catch (e) {
					this.$warning('保存登录信息失败');
				}

				// 通知父组件
				this.$emit('wechat-login-success', res.data);
				this.$emit('wechat-login');
			},

			// 检查URL中是否有微信回调参数
			checkUrlForWechatCallback() {
				const urlParams = new URLSearchParams(window.location.search);
				const code = urlParams.get('code');
				const state = urlParams.get('state');

				if (code && this.isWeChatBrowser()) {
					// 自动处理微信回调
					setTimeout(() => {
						this.handleWechatCallback(code, state);
					}, 500);
				}
			}
		},
		mounted() {
			// 组件挂载时检查URL是否有微信回调参数
			this.checkUrlForWechatCallback();
		}
	};
</script>

<style scoped>
	.social-login {
		margin-top: 25px;
	}

	.divider {
		text-align: center;
		position: relative;
		margin-bottom: 20px;
		color: #999;
		font-size: 13px;
	}

	.divider::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 0;
		right: 0;
		height: 1px;
		background: #e0e0e0;
	}

	.divider span {
		background: white;
		padding: 0 15px;
		position: relative;
	}

	.btn {
		width: 100%;
		padding: 12px;
		border: none;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.3s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.btn:hover {
		transform: translateY(-1px);
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
	}

	.btn:active {
		transform: translateY(0);
	}

	.btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		transform: none !important;
		box-shadow: none !important;
	}

	.btn-wechat {
		background: #07C160;
		color: white;
		box-shadow: 0 3px 10px rgba(7, 193, 96, 0.3);
	}

	.btn-wechat:hover:not(:disabled) {
		background: #06ae56;
		box-shadow: 0 5px 15px rgba(7, 193, 96, 0.4);
	}

	.btn-wechat i {
		font-size: 16px;
	}

	.el-icon-loading {
		animation: rotating 2s linear infinite;
	}

	@keyframes rotating {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}
</style>